import { index } from "d3";
import { worldGroupDatum } from "@atlas-int/data";

/** Single product-year datum from the input data */
interface ProductYearDatum {
  year: number;
  productId: string;
  exportValue: number;
  importValue?: number;
  globalMarketShare?: number;
  [key: string]: unknown;
}

/** Group data entry within a product year */
interface GroupDataEntry {
  productId: string;
  exportValue: number;
  [key: string]: unknown;
}

/** Global product year data with group breakdown */
interface GlobalProductYearEntry {
  groupYear: string;
  year: number;
  groupData: GroupDataEntry[];
  [key: string]: unknown;
}

interface TransformProductsParams {
  exporter: string | undefined;
  inputData: ProductYearDatum[];
  productYearData: GlobalProductYearEntry[];
}

const transformProducts = ({ exporter, inputData, productYearData }: TransformProductsParams) => {
  if (inputData.length === 0) {
    return undefined;
  }

  if (exporter) {
    let mappedLocationProductYearData;

    if (exporter === worldGroupDatum.groupId) {
      /*
            NOTE: When the selected exporter is `World`, we will coerce all product-year
            values to 100% of total market share; this is to smooth over variations in the
            individual country data that may yield `globalMarketShare` values > 100% for specific
            products.
            */

      mappedLocationProductYearData = inputData
        .map((productYearDatum: ProductYearDatum) => {
          return {
            ...productYearDatum,
            globalMarketShare: 1,
          };
        })
        .sort((a, b) => +a.year - +b.year);
    } else {
      const productYearDataLookup = index(
        productYearData,
        (d: GlobalProductYearEntry) => d.groupYear,
      );
      mappedLocationProductYearData = inputData
        .map((productYearDatum: ProductYearDatum) => {
          let {
            year,
            productId: sectorId,
            exportValue: locationExportValue,
          } = productYearDatum;

          let globalExportValue: number | undefined, globalMarketShare: number | undefined;

          let matchingGlobalProductYearData = productYearDataLookup.get(year as unknown as string);
          if (matchingGlobalProductYearData) {
            let { groupData } = matchingGlobalProductYearData;
            let matchingSectorData = groupData.find(
              (entry: GroupDataEntry) => entry.productId === sectorId,
            );
            if (matchingSectorData) {
              globalExportValue = matchingSectorData.exportValue;
            }
          }

          if (globalExportValue) {
            globalMarketShare = locationExportValue / globalExportValue;
          }

          return {
            ...productYearDatum,
            globalMarketShare,
          };
        })
        .sort((a, b) => +a.year - +b.year);
    }

    return mappedLocationProductYearData;
  }
};

export default transformProducts;
